import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { AppNotification } from "@/lib/notifications";

const TABLE = "thong_bao";
const LIMIT = 50;

/** So sánh để không tạo mảng mới khi không có gì thay đổi (tránh vẽ lại vô cớ). */
function isSameList(a: AppNotification[], b: AppNotification[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((row, index) => {
    const other = b[index];
    return (
      row.id === other.id &&
      row.da_doc === other.da_doc &&
      row.thoi_gian_tao === other.thoi_gian_tao
    );
  });
}

// Quản lý danh sách thông báo trong app dành cho admin / quản lý kho.
// Lắng nghe realtime để chuông tự cập nhật ngay khi có thông báo mới,
// không cần tải lại trang.
export function useNotifications(enabled: boolean) {
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchItems = useCallback(
    async (silent = false) => {
      if (!enabled) {
        setItems([]);
        setInitialized(false);
        return;
      }
      // Đang có một lần tải chạy rồi thì bỏ qua, không xếp hàng request.
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      if (!silent) setLoading(true);
      setError(null);
      try {
        const { data, error: queryError } = await supabase
          .from(TABLE)
          .select("id, loai, tieu_de, noi_dung, ma_van_don, da_doc, thoi_gian_tao")
          .order("thoi_gian_tao", { ascending: false })
          .limit(LIMIT);
        if (queryError) throw queryError;
        if (!mountedRef.current) return;
        const next = (data ?? []) as AppNotification[];
        setItems((prev) => (isSameList(prev, next) ? prev : next));
      } catch {
        if (mountedRef.current) setError("Không thể tải thông báo.");
      } finally {
        inFlightRef.current = false;
        if (mountedRef.current) {
          setLoading(false);
          setInitialized(true);
        }
      }
    },
    [enabled]
  );

  // `refresh` dùng cho lưới an toàn định kỳ: cập nhật ngầm, không bật loading.
  const refresh = useCallback(() => {
    void fetchItems(true);
  }, [fetchItems]);

  useEffect(() => {
    void fetchItems(false);
  }, [fetchItems]);

  useEffect(() => {
    if (!enabled) return;
    const channel = supabase
      .channel("thong-bao-realtime")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: TABLE },
        (payload) => {
          const row = payload.new as AppNotification;
          setItems((prev) => {
            if (prev.some((n) => n.id === row.id)) return prev;
            return [row, ...prev].slice(0, LIMIT);
          });
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [enabled]);

  return {
    items,
    loading,
    error,
    initialized,
    refresh,
  };
}